import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Spinner } from '../components/ui.jsx'
import { Progress } from '../components/Progress.jsx'

/** Debounce sencillo: evita una petición por tecla al validar el nombre. */
function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

export function NewProject() {
  const navigate = useNavigate()
  const [paso, setPaso] = useState(1)
  const [slug, setSlug] = useState('')
  const [repo, setRepo] = useState('')
  const [branch, setBranch] = useState('main')
  const [puerto, setPuerto] = useState('3000')
  const [creando, setCreando] = useState(false)
  const [error, setError] = useState(null)
  // El slug del proyecto YA CREADO, fijado al crearlo.
  //
  // No se puede leer de `slugCheck.data.slug` a partir de aquí: esa consulta comprueba si el
  // nombre está LIBRE, y en cuanto el proyecto existe pasa a responder
  // `{ok:false, error:"ya existe"}` — sin campo `slug`. Como TanStack recarga al volver el foco
  // a la ventana, bastaba con cambiar de pestaña durante el alta para que el asistente se
  // quedara sondeando /api/projects/undefined/provisioning y no avanzara nunca del paso 3.
  const [creado, setCreado] = useState(null)

  const rules = useQuery({ queryKey: ['provisionRules'], queryFn: api.provisionRules, staleTime: Infinity })

  const slugD = useDebounced(slug)
  const repoD = useDebounced(repo)

  const slugCheck = useQuery({
    queryKey: ['checkSlug', slugD],
    queryFn: () => api.checkSlug(slugD),
    // Una vez creado no hay nada que comprobar, y preguntarlo solo puede confundir.
    enabled: slugD.length >= 3 && !creado,
  })
  const repoCheck = useQuery({
    queryKey: ['checkRepo', repoD], queryFn: () => api.checkRepo(repoD), enabled: repoD.length >= 3,
  })

  // La rama por defecto la dice el repo: no todos usan 'main'.
  useEffect(() => {
    if (repoCheck.data?.ok && repoCheck.data.defaultBranch) setBranch(repoCheck.data.defaultBranch)
  }, [repoCheck.data])

  const slugOk = slugCheck.data?.ok === true
  const repoOk = repoCheck.data?.ok === true

  const crear = async () => {
    setCreando(true); setError(null)
    try {
      const r = await api.createProject({ slug: slugCheck.data.slug, gitlabPath: repoCheck.data.path, branch, puerto: Number(puerto) || 3000 })
      // El slug bueno es el que devuelve el servidor: él es quien lo normaliza.
      setCreado(r?.slug ?? slugCheck.data.slug)
      setPaso(3)
    } catch (err) {
      setError(err.body?.error ?? err.message)
      setCreando(false)
    }
  }

  if (rules.isPending) return <div className="main" style={{ margin: '0 auto' }}><Spinner /></div>

  return (
    <div className="main" style={{ margin: '0 auto', maxWidth: 680 }}>
      <h1 className="h1">Nuevo proyecto</h1>
      <p className="lede">
        Crea el entorno completo: base de datos, dominio con certificado, folder de funciones y
        despliegue automático desde GitLab.
      </p>

      <div className="steps-bar">
        {['Identidad', 'Confirmar', 'Creando'].map((t, i) => (
          <div key={t} className={`step-pill ${paso === i + 1 ? 'active' : paso > i + 1 ? 'done' : ''}`}>
            <span className="n">{paso > i + 1 ? '✓' : i + 1}</span>{t}
          </div>
        ))}
      </div>

      {paso === 1 && (
        <div className="card card-pad" style={{ display: 'grid', gap: 18 }}>
          <div>
            <label className="field-label" htmlFor="slug">Nombre del proyecto</label>
            <input id="slug" className="input wide" value={slug} autoFocus
                   placeholder="crm" onChange={(e) => setSlug(e.target.value)} />
            <div className="field-hint">
              {slugCheck.data?.ok
                ? <span style={{ color: 'var(--success)' }}>{slugCheck.data.domain}</span>
                : slugCheck.data?.error
                  ? <span style={{ color: 'var(--danger)' }}>{slugCheck.data.error}</span>
                  : <>Minúsculas, números y guiones. Será el subdominio: <code>&lt;nombre&gt;.{rules.data.zone}</code></>}
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="repo">Repositorio de GitLab</label>
            <input id="repo" className="input wide" value={repo}
                   placeholder="interno/mi-app" onChange={(e) => setRepo(e.target.value)} />
            <div className="field-hint">
              {repoCheck.data?.ok
                ? <span style={{ color: 'var(--success)' }}>
                    {repoCheck.data.name} · rama por defecto <code>{repoCheck.data.defaultBranch}</code>
                  </span>
                : repoCheck.data?.error
                  ? <span style={{ color: 'var(--danger)' }}>{repoCheck.data.error}</span>
                  : 'Ruta completa del repo que ya existe. Da igual dónde cuelgue.'}
            </div>
          </div>

          {repoOk && (
            <div>
              <label className="field-label" htmlFor="branch">Rama que se despliega</label>
              <input id="branch" className="input" value={branch} onChange={(e) => setBranch(e.target.value)} />

              <label className="field-label" htmlFor="puerto" style={{ marginTop: 12 }}>
                Puerto donde escucha la aplicación
              </label>
              <input id="puerto" className="input" type="number" min="1" max="65535" value={puerto}
                     style={{ width: 120 }} onChange={(e) => setPuerto(e.target.value)} />
              <div className="field-hint">
                Llega a la app como <span className="mono">PORT</span>. Déjalo en 3000 salvo que
                la app no pueda elegir puerto.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" disabled={!slugOk || !repoOk} onClick={() => setPaso(2)}>Continuar</button>
            <button className="btn secondary" onClick={() => navigate('/')}>Cancelar</button>
          </div>
        </div>
      )}

      {paso === 2 && (
        <>
          <div className="card">
            <div className="card-head"><span className="h2" style={{ fontSize: 14 }}>Se va a crear</span></div>
            <div className="table-scroll">
              <table className="t">
                <tbody>
                  <tr><td style={{ color: 'var(--fg-2)', width: 210 }}>Proyecto</td><td className="mono">{slugCheck.data.slug}</td></tr>
                  <tr><td style={{ color: 'var(--fg-2)' }}>Dominio</td><td className="mono">{slugCheck.data.domain}</td></tr>
                  <tr><td style={{ color: 'var(--fg-2)' }}>Repositorio</td><td className="mono">{repoCheck.data.path}</td></tr>
                  <tr><td style={{ color: 'var(--fg-2)' }}>Rama</td><td className="mono">{branch}</td></tr>
                  <tr><td style={{ color: 'var(--fg-2)' }}>Puerto</td><td className="mono">{puerto}</td></tr>
                  <tr><td style={{ color: 'var(--fg-2)' }}>Base de datos</td><td className="mono">{slugCheck.data.slug} · rol {slugCheck.data.slug}_app</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="alert" style={{ marginTop: 14 }}>
            Quien tenga acceso al repositorio en GitLab —o a cualquiera de sus grupos padre—
            tendrá acceso al proyecto en la consola. Los permisos salen de ahí, no se gestionan aquí.
          </div>

          {error && <div className="alert error" style={{ marginTop: 14 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button className="btn" onClick={crear} disabled={creando}>
              {creando ? 'Creando…' : 'Crear proyecto'}
            </button>
            <button className="btn secondary" onClick={() => setPaso(1)} disabled={creando}>Atrás</button>
          </div>
        </>
      )}

      {paso === 3 && creado && (
        <Progress slug={creado} onDone={() => navigate(`/p/${creado}`)} />
      )}
    </div>
  )
}
